/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  COLLAPSE_VISUALS,
  UiController,
  commands,
  compareWindows,
  consoleCommands,
  events,
} from 'modules/common/ui-controller';
import {
  expandFocusOnAll,
  getHotelValuesFromList,
  getValuesFromList,
} from 'modules/common/utils/array';
import { KEYS } from 'modules/dashboard/constants';
import {
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
} from 'modules/common/ui-controller/utils/shared-command-steps';
import { webClickActions } from '../slice';
import { CLICK_STEP_LIST } from '../constants';
import { setCompareWindowCommandValue } from '../../inner-filter/functions';

const isLoaded = (context) => context.isLoaded;
// time given to the custom compare window to open before the custom dates are applied
const CUSTOM_COMPARE_WINDOW_DELAY = 2000;
// time given to the utm focus on list to be rebuilt after the utm breakdown is changed
const UTM_FOCUS_ON_DELAY = 2000;
// visual collapse command value to the action that shows or hides the visual
const VISUAL_DISPLAY_ACTIONS = {
  [COLLAPSE_VISUALS.TT]: webClickActions.setTimeTrendIsDisplay,
  [COLLAPSE_VISUALS.TP]: webClickActions.setTimePaceIsDisplay,
  [COLLAPSE_VISUALS.ST]: webClickActions.setSegmentTrendIsDisplay,
  [COLLAPSE_VISUALS.SP]: webClickActions.setSegmentPaceIsDisplay,
  [COLLAPSE_VISUALS.HT]: webClickActions.setHotelTrendIsDisplay,
  [COLLAPSE_VISUALS.HP]: webClickActions.setHotelPaceIsDisplay,
  [COLLAPSE_VISUALS.UT]: webClickActions.setUtmTrendIsDisplay,
  [COLLAPSE_VISUALS.UP]: webClickActions.setUtmPaceIsDisplay,
};
// This tab is dormant: its key is not in TAB_CONFIG of the tab container, so these commands are
// not applied until the tab is enabled again
/**
 * Web click tab filter commands in the order they are applied.
 * The position in this list is the execution order, see useCommandPipeline.
 * context is built in useWebClickCommandPipeline.
 */
const WEB_CLICK_FILTER_COMMAND_STEPS = [
  {
    command: consoleCommands.BOOKING_DATE,
    event: events.SET_WEB_CLICK_CUSTOM_BOOKING,
    // open custom booking date picker, the date range is applied once it is opened
    prepare: (value, { dispatch }) => {
      if (value !== commands.OTB) {
        dispatch(webClickActions.setWebClickOtb(true));
      }
    },
    ready: (context, { pending }) =>
      context.isLoaded &&
      (pending[consoleCommands.BOOKING_DATE] === commands.OTB || Boolean(context.customBooking)),
    apply: (value, { dispatch, resetBookingDate }) => {
      // checks if booking date range value is 'otb'
      if (value === commands.OTB) {
        dispatch(webClickActions.setWebClickOtb(false));
        dispatch(webClickActions.setWebClickBookingDate(resetBookingDate));
      } else {
        // emit event to set booking date range
        UiController.setBookingDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.WEB_CLICK_STEPS,
    event: events.SET_WEB_CLICK_STEPS,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      // get web click steps options
      const optionsSelected = getValuesFromList(value, CLICK_STEP_LIST);
      if (optionsSelected?.length > 0) {
        dispatch(webClickActions.setWebClickStep(optionsSelected));
      }
    },
  },
  {
    command: consoleCommands.WEB_CLICK_SCALE,
    event: events.SET_WEB_CLICK_SCALE,
    ready: isLoaded,
    apply: (value, { setScaleInputValue }) => {
      const scale = parseInt(value, 10);
      if (scale >= 0 && scale <= 100) {
        setScaleInputValue(scale);
      }
    },
  },
  {
    command: consoleCommands.UTM_BREAKDOWN,
    event: events.SET_WEB_CLICK_UTM_BREAKDOWN,
    ready: (context) => context.isLoaded && context.utmBreakdownList?.length > 0,
    apply: (value, { dispatch }) => {
      dispatch(webClickActions.setSelectedUtmBreakdown(value));
      dispatch(webClickActions.setUtmFocusOn([]));
    },
  },
  {
    command: consoleCommands.UTM_FOCUS_ON,
    event: events.SET_WEB_CLICK_UTM_FOCUS_ON,
    ready: (context) => context.isLoaded && context.utmFocusOnList?.length > 0,
    delayBefore: UTM_FOCUS_ON_DELAY,
    apply: (value, { dispatch, utmFocusOnList }) => {
      // get corresponding focus on value and execute dispatch function
      const optionsSelected = getValuesFromList(value, utmFocusOnList);
      dispatch(webClickActions.setUtmFocusOn(optionsSelected?.length > 0 ? optionsSelected : []));
    },
  },
  {
    command: consoleCommands.COMPARE_HOTEL,
    event: events.SET_WEB_CLICK_COMPARE_HOTELS,
    ready: (context) => context.isLoaded && context.hotelList?.length > 0,
    apply: (value, { dispatch, hotelList }) => {
      const hotelsSelected = getHotelValuesFromList(value, hotelList);
      if (hotelsSelected?.length > 0) {
        dispatch(webClickActions.setEnableSecondaryHotelGroups(false));
        dispatch(webClickActions.setWebClickHotelFocusOn(hotelsSelected[0]));
      }
    },
  },
  {
    command: consoleCommands.COMPARE_HOTEL_GROUP,
    event: events.SET_WEB_CLICK_HOTEL_GROUP,
    ready: (context) => context.isLoaded && context.hotelGroupList?.length > 0,
    apply: (value, { dispatch, hotelGroupList }) => {
      const hotelGroupsSelected = getHotelValuesFromList(value, hotelGroupList);
      if (hotelGroupsSelected?.length > 0) {
        dispatch(webClickActions.setEnableSecondaryHotelGroups(true));
        dispatch(webClickActions.setWebClickHotelFocusOn(hotelGroupsSelected[0]));
      }
    },
  },
  {
    command: consoleCommands.NORMALIZED_VIEW,
    event: events.SET_WEB_CLICK_NORMALIZED_VIEW,
    ready: (context) => context.isLoaded && context.hotelVisualFilterList?.length > 0,
    apply: (value, { dispatch }) => {
      // execute dispatch function to set normalized view state
      dispatch(webClickActions.setWebClickNormalizedView(value === 1));
    },
  },
  {
    command: consoleCommands.ALIGNMENT,
    event: events.WEB_CLICK_ALIGNMENT,
    ready: (context) => context.isLoaded && Boolean(context.visualFilters),
    apply: (value, { handleToggleChange }) => {
      handleToggleChange({ target: { checked: value } });
    },
  },
  {
    // custom dates are applied by the next step, only with the custom compare window
    command: consoleCommands.COMPARE_WINDOW,
    requires: [consoleCommands.CUSTOM_DATES],
    event: events.SET_WEB_CLICK_COMPARE_WINDOW,
    ready: (context) => context.isLoaded && Boolean(context.dateComparison),
    apply: (value, { customBooking, handleComparisonChange }) => {
      // check if 'otb' value selected as compare window and custom booking is false
      setCompareWindowCommandValue(value, customBooking, handleComparisonChange);
      return compareWindows[value] === KEYS.CUSTOM
        ? [consoleCommands.COMPARE_WINDOW]
        : [consoleCommands.COMPARE_WINDOW, consoleCommands.CUSTOM_DATES];
    },
    delayAfter: (value) =>
      compareWindows[value] === KEYS.CUSTOM ? CUSTOM_COMPARE_WINDOW_DELAY : 0,
  },
  {
    command: consoleCommands.CUSTOM_DATES,
    event: events.SET_WEB_CLICK_CUSTOM_DATE_RANGE,
    ready: (context, { keys }) =>
      context.isLoaded &&
      (context.dateComparison === KEYS.CUSTOM || !keys.includes(consoleCommands.COMPARE_WINDOW)),
    apply: (value, { dispatch, dateComparison }) => {
      // checks if date comparison value is 'custom'
      if (dateComparison === KEYS.CUSTOM) {
        const datesExtracted = value?.split('-');
        // reset custom dates
        dispatch(webClickActions.setWebClickCustomDate(datesExtracted[0]));
        // emit event to set custom date range
        UiController.setCustomDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.PACE_HORIZON,
    event: events.SET_WEB_CLICK_PACE_HORIZON,
    ready: (context) => context.isLoaded && context.paceList?.length > 0,
    apply: (value, { dispatch, paceList }) => {
      // get pace horizon value
      const paces = paceList?.filter((pace) => pace?.label[0].includes(value));
      if (paces?.length > 0) {
        dispatch(webClickActions.setWebClickPaceHorizon(paces[0]?.id));
      }
    },
  },
  {
    command: consoleCommands.FOCUS,
    event: events.SET_WEB_CLICK_FOCUS_ON,
    // wait until the focus on list belongs to the selected breakdown
    ready: (context) =>
      context.isLoaded && context.isFocusOnListReady && context.focusOnList?.length > 0,
    apply: (value, { dispatch, focusOnList }) => {
      // replace ALL command value with every focus on option
      const optionsSelected = getValuesFromList(expandFocusOnAll(value, focusOnList), focusOnList);
      dispatch(
        webClickActions.setWebClickBreakdownFocusOn(
          optionsSelected?.length > 0 ? optionsSelected : []
        )
      );
    },
  },
  {
    command: consoleCommands.SYMMETRIC_COMPARISON_COLLAPSE,
    event: events.SET_WEB_CLICK_SYMMETRIC_COMPARISON_COLLAPSE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(webClickActions.setSymmetricComparisonIsDisplay(value === 1));
    },
  },
  {
    command: consoleCommands.VISUAL_LOADING,
    event: events.SET_WEB_CLICK_VISUAL_COLLAPSE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      value?.split(',')?.forEach((visual) => {
        // get the visual collapse enable/disable status
        const status = parseInt(visual?.substring(2), 10);
        // get the type of visual view
        const setIsDisplay = VISUAL_DISPLAY_ACTIONS[visual?.substring(0, 2)];
        if (setIsDisplay) {
          dispatch(setIsDisplay(status === 1));
        }
      });
    },
  },
];

/**
 * Web click tab commands of a command url in the order they are applied
 */
const WEB_CLICK_COMMAND_STEPS = [
  ...WEB_CLICK_FILTER_COMMAND_STEPS,
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
];

export default WEB_CLICK_COMMAND_STEPS;
